import PropTypes from "prop-types";
import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Collapse,
  Popover,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import toast from "react-hot-toast";
import { usePopover } from "@hooks/shared/use-popover";
import useAssetTreeStore from "@stores/client/useAssetTreeStore";
import { TreeItemIcon } from "./tree-item-icon";
import { ExpandableSidebarStateContext } from "@context/shared/ExpandableSidebarState";
import { AssetHoverCard } from "../asset-hover-card";
import { useScrollIntoViewOnSelect } from "../hooks/use-scroll-into-view-on-select";

const indentPerLevel = 1.25;
export const TreeItem = (props) => {
  const { item, level } = props;
  const { t } = useTranslation();
  const { state } = useContext(ExpandableSidebarStateContext);
  const {
    itemNodes,
    nodesOpen,
    setNodesOpen,
    currentRootId,
    getAssetTreeItem,
  } = useAssetTreeStore();
  const [isOpen, setIsOpen] = useState(false);
  const [fetchingChildren, setFetchingChildren] = useState(false);
  const popover = usePopover();
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const route = item.route;
  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };
  //this helps in opening the correct node when navigating through the list as well
  useEffect(() => {
    setIsOpen(nodesOpen[item.path] || false);
  }, [nodesOpen, item.path]);
  const isSelected = pathname.endsWith(route.toString());

  // Processes only ever have transaction children, and transactions are
  // filtered out of the tree below — so a process never actually has
  // anything to expand into, even when the API says hasChildren: true.
  const isExpandable = item.hasChildren && item.type !== 2;

  //hook to make sure the selected tree item scrolls into view on page load
  useScrollIntoViewOnSelect(popover.anchorRef, isSelected);

  const handleItemClick = async (e) => {
    e.stopPropagation();
    //fetching children while routing

    // removed the check for itemNodes to ensure it fetches the latest children every time we click,
    // as the tree structure might have changed due to updates like favouriting/unfavouriting,
    // adding or deleting assets
    // which might not be reflected in the itemNodes state immediately due to async nature of state updates and to avoid potential stale data issues
    // if (item.hasChildren && !itemNodes[item.path]) {
    if (isExpandable) {
      setFetchingChildren(true);
      try {
        await getAssetTreeItem(item.path);
        setFetchingChildren(false);
        setIsOpen(true);
        setNodesOpen(item.path, true);
      } catch (error) {
        console.error("Failed to fetch asset tree children:", error);
        setFetchingChildren(false);
        toast.error(t("assets.fetchChildrenError"));
      }
    }
    navigate(`/assets/${currentRootId}/${route}`);
  };
  return (
    <>
      <ListItemButton
        selected={isSelected}
        disabled={fetchingChildren}
        sx={{
          borderRadius: "8px",
          marginRight: 1,
          pl: 0.25,
          pr: 1,
          py: 1,
          pointerEvents: state.type === "dragging" ? "none" : "auto",
          opacity: fetchingChildren ? 0.6 : 1,
          backgroundColor: popover.open
            ? "rgba(17, 25, 39, 0.04)"
            : "transparent",
        }}
        ref={popover.anchorRef}
        onMouseEnter={popover.handleOpen}
        onMouseLeave={popover.handleClose}
        onClick={handleItemClick}
        data-testid="asset-tree-item"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 0.75,
            paddingLeft: level * indentPerLevel,
            width: "100%",
          }}
        >
          <TreeItemIcon
            item={{ ...item, hasChildren: isExpandable }}
            onToggle={handleToggle}
            isExpanded={isOpen}
            isSelected={isSelected}
          />
          <ListItemText
            sx={{
              flex: 1,
              minWidth: 0,
              my: 0,
              "& .MuiListItemText-primary": {
                fontSize: "0.875rem",
                fontWeight: isSelected ? 700 : 400,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: "pointer",
                color: isSelected ? "primary.dark" : "text.primary",
              },
            }}
            primary={item.title}
          />
          <Popover
            sx={{
              pointerEvents: "none",
              transform: "translateX(2px)",
            }}
            open={popover.open}
            anchorEl={popover.anchorRef.current}
            anchorOrigin={{
              vertical: "center",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "left",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onClose={popover.handleClose}
            disableRestoreFocus
            slotProps={{
              paper: {
                sx: {
                  pointerEvents: "auto",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                },
                onMouseEnter: popover.handleOpen,
                onMouseLeave: popover.handleClose,
              },
            }}
          >
            <AssetHoverCard item={item} hasBreadcrumb={true} />
          </Popover>
        </Box>
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        {isExpandable && isOpen && itemNodes[item.path] ? (
          <>
            {itemNodes[item.path]
              // Transactions don't have their own page anymore — they're
              // only reachable from within their parent process's own
              // content view, so keep them out of the tree.
              .filter((child) => child.type !== 3)
              .map((child) => {
                return (
                  <TreeItem item={child} key={child.id} level={level + 1} />
                );
              })}
          </>
        ) : null}
      </Collapse>
    </>
  );
};
TreeItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    path: PropTypes.string.isRequired,
    hasChildren: PropTypes.bool.isRequired,
    route: PropTypes.string.isRequired,
  }).isRequired,
  level: PropTypes.number.isRequired,
};
